import {
  Node,
  Position,
  InternalNode,
  NodePositionChange,
  XYPosition,
  NodeChange,
  NodeDimensionChange,
} from '@xyflow/react';

import { Cell, CanvasNode } from '@/explore/types';
import { isConversationPart } from '@/core/cell';
import { notNil } from '@/lib/utils';

import CustomNode from './canvas-node';
import CustomGroup from './canvas-group';
import { CustomEdge } from './canvas-edge';

export const DefaultWidth = 600;
export const DefaultHeight = 400;
export const DefaultConversationWidth = 840;
export const DefaultConversationHeight = 848; // Likely output: chat + table + chat
const ViewportPadding = 40;

export const CanvasNodeType = 'canvasBlock';
export const CanvasGroupType = 'canvasGroup';

export const nodeTypes = { [CanvasNodeType]: CustomNode, [CanvasGroupType]: CustomGroup };
export const edgeTypes = { canvasEdge: CustomEdge };

// Returns the position (top|right|bottom|right) compared to the passed node
function getParams(nodeA: InternalNode, nodeB: InternalNode) {
  const centerA = getNodeCenter(nodeA);
  const centerB = getNodeCenter(nodeB);
  const horizontalDiff = Math.abs(centerA.x - centerB.x);
  const verticalDiff = Math.abs(centerA.y - centerB.y);
  let position;

  if (horizontalDiff > verticalDiff) {
    position = centerA.x > centerB.x ? Position.Left : Position.Right;
  } else {
    position = centerA.y > centerB.y ? Position.Top : Position.Bottom;
  }

  const [x, y] = getHandleCoordsByPosition(nodeA, position);
  return [x, y, position] as const;
}

function getHandleCoordsByPosition(node: InternalNode, handlePosition: Position) {
  // All handles are from type source, that's why we use handleBounds.source here
  const handle = node.internals.handleBounds?.source?.find((h) => h.position === handlePosition);

  if (!handle) {
    return [0, 0];
  }

  let offsetX = handle.width / 2;
  let offsetY = handle.height / 2;

  // The handle position that gets calculated has the origin top-left, so depending which side we are using, we add a little offset
  // when the handlePosition is Position.Right for example, we need to add an offset as big as the handle itself in order to get the correct position
  switch (handlePosition) {
    case Position.Left:
      offsetX = 0;
      break;
    case Position.Right:
      offsetX = handle.width;
      break;
    case Position.Top:
      offsetY = 0;
      break;
    case Position.Bottom:
      offsetY = handle.height;
      break;
  }

  const x = node.internals.positionAbsolute.x + handle.x + offsetX;
  const y = node.internals.positionAbsolute.y + handle.y + offsetY;

  return [x, y];
}

function getNodeCenter(node: InternalNode) {
  return {
    x: node.internals.positionAbsolute.x + (node.measured?.width ?? 0) / 2,
    y: node.internals.positionAbsolute.y + (node.measured?.height ?? 0) / 2,
  };
}

export function getEdgeParams(source: InternalNode, target: InternalNode) {
  const [sx, sy, sourcePos] = getParams(source, target);
  const [tx, ty, targetPos] = getParams(target, source);

  return {
    sx,
    sy,
    tx,
    ty,
    sourcePos,
    targetPos,
  };
}

interface Viewport {
  x: number;
  x2: number;
  y: number;
  y2: number;
}

export const getCanvasCenterPos = (canvasViewport: Viewport) => ({
  x: canvasViewport.x + (canvasViewport.x2 - canvasViewport.x) / 2,
  y: canvasViewport.y + (canvasViewport.y2 - canvasViewport.y) / 2,
});

interface convertCellToNodeParams {
  cellId: string;
  y: number;
  canvasViewport: Viewport;
  selectedCellId?: string;
  zIndex: number;
}

export const convertCellToNode = ({
  cellId,
  y,
  canvasViewport,
  zIndex,
  selectedCellId,
}: convertCellToNodeParams) => {
  const canvasCenterPos = getCanvasCenterPos(canvasViewport);

  return {
    type: CanvasNodeType,
    id: cellId,
    position: {
      x: canvasCenterPos.x - DefaultWidth / 2,
      y,
    },
    measured: { height: DefaultHeight, width: DefaultWidth },
    selected: cellId === selectedCellId,
    zIndex,
    data: {},
  };
};

interface convertConversationCellToNodeParams {
  cellId: string;
  y: number;
  canvasViewport: Viewport;
  selectedCellId?: string;
  zIndex: number;
  node: CanvasNode;
}

export const convertConversationCellToNode = ({
  cellId,
  zIndex,
  y,
  canvasViewport,
  selectedCellId,
  node,
}: convertConversationCellToNodeParams) => {
  const canvasCenterPos = getCanvasCenterPos(canvasViewport);

  return {
    type: CanvasNodeType,
    id: cellId,
    position: node.position ?? {
      x: canvasCenterPos.x - DefaultWidth / 2,
      y,
    },
    measured: {
      height: node.measured.height,
      width: node.measured.width,
    },
    selected: cellId === selectedCellId,
    zIndex,
    data: {},
  };
};

export interface ConvertCellsToNodesParams {
  cells: Cell[];
  selectedCell: Cell | null;
  canvasViewport: Viewport;
  persistedNodes: CanvasNode[];
}

/**
 * Enhances cells with data that is necessary for CanvasNode to pass on as props into ExplorationCell
 */
export const convertCellsToNodes = ({
  cells,
  selectedCell,
  canvasViewport,
  persistedNodes,
}: ConvertCellsToNodesParams): Node[] => {
  const conversationCells = cells.filter(isConversationPart);
  const canvasCenterPosition = getCanvasCenterPos(canvasViewport);
  const nextZIndex = persistedNodes.reduce((highest, node) => Math.max(highest, node.zIndex), 0);
  const newNodes = cells.filter(
    (cell) => !isConversationPart(cell) && persistedNodes.every((n) => n.id !== cell.id),
  );
  // Stack all new nodes vertically
  let yOffset = 0;

  const nodes = cells
    .filter((cell) => !isConversationPart(cell))
    .map((cell) => {
      let node = persistedNodes.find((n) => n.id === cell.id);

      if (node === undefined) {
        const canvasMidPointY = Math.max(
          canvasViewport.y + ViewportPadding,
          canvasCenterPosition.y - DefaultHeight / 2,
        );
        node = convertCellToNode({
          cellId: cell.id,
          // If only a single new node is being positioned, place it to the center of the canvas
          y: newNodes.length === 1 ? canvasMidPointY : yOffset,
          canvasViewport,
          selectedCellId: selectedCell?.id,
          zIndex: nextZIndex + 1,
        });
        yOffset += node.measured.height + ViewportPadding;
      }

      if (node.precomputed === true) {
        node = convertConversationCellToNode({
          cellId: cell.id,
          y: yOffset,
          canvasViewport,
          selectedCellId: selectedCell?.id,
          zIndex: nextZIndex + 1,
          node,
        });
        yOffset += node.measured.height + ViewportPadding;
      }

      return {
        type: CanvasNodeType,
        id: cell.id,
        position: node.position,
        measured: node.measured,
        zIndex: node.zIndex,
        selected: cell.id === selectedCell?.id,
        data: {},
      };
    })
    .concat(
      conversationCells.reduce<any[]>((result, cell) => {
        const conversationId = cell.conversationId;
        const existingGroup = result.find((group) => group.id === conversationId);

        if (existingGroup === undefined) {
          const nodeOptions = persistedNodes.find((n) => n.id === conversationId);
          const width = DefaultConversationWidth;
          const height = cell.viewOptions?.height ?? DefaultConversationHeight;
          const x = nodeOptions?.position.x ?? canvasCenterPosition.x - width / 2;
          const y =
            nodeOptions?.position.y ??
            Math.max(canvasViewport.y + ViewportPadding, canvasCenterPosition.y - height / 2);

          result.push({
            id: conversationId,
            type: CanvasGroupType,
            position: { x, y },
            measured: { height, width },
            zIndex: nodeOptions?.zIndex ?? 999,
            draggable: true,
            resizable: false,
            selectable: true,
            connectable: false,
            deletable: false,
            data: {
              cellIds: [cell.id],
            },
          });
        } else {
          existingGroup.data.cellIds.push(cell.id);
        }

        return result;
      }, []),
    );

  return adjustZIndexes(nodes);
};

export const transformNodesToPersisted = (nodes: Node[]) => {
  return adjustZIndexes(nodes).map((node) => ({
    id: node.id,
    position: node.position,
    zIndex: node.zIndex ?? 0,
    measured: {
      height: node.measured?.height ?? DefaultHeight,
      width: node.measured?.width ?? DefaultWidth,
    },
    type: node.type ?? CanvasNodeType,
  }));
};

const incrementZIndexes = (nodes: Node[], nodeIdsToShift: string[]) => {
  let currentMaxZIndex = Math.max(...nodes.map((node) => node.zIndex ?? 0)) ?? 0;
  const adjustedZIndex = nodes.map((node) =>
    nodeIdsToShift.includes(node.id)
      ? {
          ...node,
          zIndex: ++currentMaxZIndex,
        }
      : node,
  );
  return adjustedZIndex;
};

/*
 * Adjusts the zIndex of the nodes so that the selected nodes have the highest zIndex
 * and the rest of the nodes have the zIndex in the order they appear in the array
 */
export const adjustZIndexes = (nodes: Node[]) => {
  const selectedNodeIds = nodes.filter((node) => node.selected ?? false).map((node) => node.id);

  if (selectedNodeIds.length === 0) {
    return nodes;
  }

  return incrementZIndexes(nodes, selectedNodeIds)
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0))
    .map((node, index) => ({ ...node, zIndex: index }));
};

export const isDimensionChange = (change: NodeChange): change is NodeDimensionChange =>
  change.type === 'dimensions' && change.resizing === true;

export const isPositionChange = (change: NodeChange): change is NodePositionChange =>
  change.type === 'position';

type GetHelperLinesResult = {
  horizontal?: number;
  vertical?: number;
  snapPosition: Partial<XYPosition>;
  snapEdges: { top: boolean; left: boolean; right: boolean; bottom: boolean };
};

interface MovingEdges {
  left: boolean;
  right: boolean;
  top: boolean;
  bottom: boolean;
}

function getMovingEdges(
  nodeA: Node,
  positionChange?: NodePositionChange,
  dimensionChange?: NodeDimensionChange,
): MovingEdges {
  if (dimensionChange?.resizing !== true) {
    return { left: true, right: true, top: true, bottom: true };
  }

  const posChanged = {
    x: positionChange?.position !== undefined && positionChange.position.x !== nodeA.position.x,
    y: positionChange?.position !== undefined && positionChange.position.y !== nodeA.position.y,
  };

  const dimChanged = {
    width:
      dimensionChange?.dimensions !== undefined &&
      dimensionChange.dimensions.width !== (nodeA.measured?.width ?? 0),
    height:
      dimensionChange?.dimensions !== undefined &&
      dimensionChange.dimensions.height !== (nodeA.measured?.height ?? 0),
  };

  return {
    left: posChanged.x,
    top: posChanged.y,
    // The right edge moves if the width changes BUT the left edge is stationary.
    right: dimChanged.width && !posChanged.x,
    // The bottom edge moves if the height changes BUT the top edge is stationary.
    bottom: dimChanged.height && !posChanged.y,
  };
}

/**
 * This utility function can be called with a position change (inside canvas onNodesChange),
 * it checks all other nodes, calculates the helper line positions and the position where the current node should snap to
 */
export function getHelperLines(
  changes: NodeChange[],
  nodes: Node[],
  distance = 8,
): GetHelperLinesResult {
  const defaultResult = {
    horizontal: undefined,
    vertical: undefined,
    snapPosition: { x: undefined, y: undefined },
    snapEdges: { top: false, left: false, right: false, bottom: false },
  };

  const dimensionChange = changes.find(isDimensionChange);
  const positionChange = changes.find(
    (change): change is NodePositionChange => change.type === 'position',
  );

  if (!positionChange && !dimensionChange) {
    return defaultResult;
  }

  const nodeId = positionChange?.id ?? dimensionChange?.id;
  if (nodeId === undefined) {
    return defaultResult;
  }

  const nodeA = nodes.find((node) => node.id === nodeId);
  if (!nodeA) {
    return defaultResult;
  }

  // If we have both changes, they should be for the same node
  if (positionChange && dimensionChange && positionChange.id !== dimensionChange.id) {
    return defaultResult;
  }

  const movingEdges = getMovingEdges(nodeA, positionChange, dimensionChange);
  const currentPosition = positionChange?.position ?? nodeA.position;
  const currentDimensions = dimensionChange?.dimensions ?? {
    width: nodeA.measured?.width ?? 0,
    height: nodeA.measured?.height ?? 0,
  };

  const nodeABounds = {
    left: currentPosition.x,
    right: currentPosition.x + currentDimensions.width,
    top: currentPosition.y,
    bottom: currentPosition.y + currentDimensions.height,
    width: currentDimensions.width,
    height: currentDimensions.height,
  };

  let horizontalDistance = distance;
  let verticalDistance = distance;

  return nodes
    .filter((node) => node.id !== nodeA.id)
    .reduce<GetHelperLinesResult>((result, nodeB) => {
      const nodeBBounds = {
        left: nodeB.position.x,
        right: nodeB.position.x + (nodeB.measured?.width ?? 0),
        top: nodeB.position.y,
        bottom: nodeB.position.y + (nodeB.measured?.height ?? 0),
        width: nodeB.measured?.width ?? 0,
        height: nodeB.measured?.height ?? 0,
      };

      if (movingEdges.left) {
        const distanceLeftLeft = Math.abs(nodeABounds.left - nodeBBounds.left);
        if (distanceLeftLeft < verticalDistance) {
          result.snapPosition.x = nodeBBounds.left;
          result.vertical = nodeBBounds.left;
          result.snapEdges.left = true;
          verticalDistance = distanceLeftLeft;
        }

        const distanceLeftRight = Math.abs(nodeABounds.left - nodeBBounds.right);
        if (distanceLeftRight < verticalDistance) {
          result.snapPosition.x = nodeBBounds.right;
          result.vertical = nodeBBounds.right;
          result.snapEdges.left = true;
          verticalDistance = distanceLeftRight;
        }
      }

      if (movingEdges.right) {
        const distanceRightRight = Math.abs(nodeABounds.right - nodeBBounds.right);
        if (distanceRightRight < verticalDistance) {
          result.snapPosition.x = nodeBBounds.right - nodeABounds.width;
          result.vertical = nodeBBounds.right;
          result.snapEdges.right = true;
          verticalDistance = distanceRightRight;
        }

        const distanceRightLeft = Math.abs(nodeABounds.right - nodeBBounds.left);
        if (distanceRightLeft < verticalDistance) {
          result.snapPosition.x = nodeBBounds.left - nodeABounds.width;
          result.vertical = nodeBBounds.left;
          result.snapEdges.right = true;
          verticalDistance = distanceRightLeft;
        }
      }

      if (movingEdges.top) {
        const distanceTopTop = Math.abs(nodeABounds.top - nodeBBounds.top);
        if (distanceTopTop < horizontalDistance) {
          result.snapPosition.y = nodeBBounds.top;
          result.horizontal = nodeBBounds.top;
          result.snapEdges.top = true;
          horizontalDistance = distanceTopTop;
        }

        const distanceTopBottom = Math.abs(nodeABounds.top - nodeBBounds.bottom);
        if (distanceTopBottom < horizontalDistance) {
          result.snapPosition.y = nodeBBounds.bottom;
          result.horizontal = nodeBBounds.bottom;
          result.snapEdges.top = true;
          horizontalDistance = distanceTopBottom;
        }
      }

      if (movingEdges.bottom) {
        const distanceBottomBottom = Math.abs(nodeABounds.bottom - nodeBBounds.bottom);
        if (distanceBottomBottom < horizontalDistance) {
          result.snapPosition.y = nodeBBounds.bottom - nodeABounds.height;
          result.horizontal = nodeBBounds.bottom;
          result.snapEdges.bottom = true;
          horizontalDistance = distanceBottomBottom;
        }

        const distanceBottomTop = Math.abs(nodeABounds.bottom - nodeBBounds.top);
        if (distanceBottomTop < horizontalDistance) {
          result.snapPosition.y = nodeBBounds.top - nodeABounds.height;
          result.horizontal = nodeBBounds.top;
          result.snapEdges.bottom = true;
          horizontalDistance = distanceBottomTop;
        }
      }

      return result;
    }, defaultResult);
}

/**
 * Compensate in either node x/y position or in node's height/width, depending on what direction we've snapped to
 */
export function adjustChanges({
  changes,
  nodes,
  lines,
}: {
  changes: NodeChange[];
  nodes: Node[];
  lines: GetHelperLinesResult;
}) {
  if (lines.horizontal === undefined && lines.vertical === undefined) {
    return changes;
  }

  const positionChange = changes.find(isPositionChange);
  const dimensionChange = changes.find(isDimensionChange);

  if (positionChange?.position) {
    const oldPosY = positionChange.position.y;
    const oldPosX = positionChange.position.x;
    positionChange.position.x = lines.snapPosition.x ?? positionChange.position.x;
    positionChange.position.y = lines.snapPosition.y ?? positionChange.position.y;

    if (
      (lines.horizontal !== undefined || lines.vertical !== undefined) &&
      dimensionChange?.dimensions
    ) {
      const heightDelta =
        lines.snapEdges.bottom || lines.snapEdges.top
          ? Math.floor(positionChange.position.y - oldPosY)
          : 0;
      const widthDelta =
        lines.snapEdges.left || lines.snapEdges.right
          ? Math.floor(positionChange.position.x - oldPosX)
          : 0;
      if (lines.snapEdges.top && heightDelta !== 0) {
        dimensionChange.dimensions.height -= heightDelta;
      } else if (lines.snapEdges.bottom && heightDelta !== 0) {
        positionChange.position.y -= heightDelta;
      }

      if (lines.snapEdges.left && widthDelta !== 0) {
        dimensionChange.dimensions.width -= widthDelta;
      } else if (lines.snapEdges.right && widthDelta !== 0) {
        positionChange.position.x -= widthDelta;
      }
    }
  } else if (dimensionChange?.dimensions) {
    const nodePos = nodes.find((node) => node.id === dimensionChange.id)?.position;
    dimensionChange.dimensions.height =
      lines.horizontal !== undefined && nodePos !== undefined
        ? Math.abs(lines.horizontal - nodePos.y)
        : dimensionChange.dimensions.height;
    dimensionChange.dimensions.width =
      lines.vertical !== undefined && nodePos !== undefined
        ? Math.abs(lines.vertical - nodePos.x)
        : dimensionChange.dimensions.width;
  }

  return changes;
}

export const measureCanvasNodes = (
  wrapperElement: HTMLDivElement,
  cellIdsToMeasure: string[] = [],
): CanvasNode[] => {
  const cellElements = [...(wrapperElement.querySelectorAll('[data-id]') ?? [])];

  return cellElements
    .map((element, idx) => {
      const cellId = element.getAttribute('data-id') as string;
      if (cellIdsToMeasure.length > 0 && !cellIdsToMeasure.includes(cellId)) {
        return null;
      }
      return measureCanvasNode(element, cellId, idx);
    })
    .filter(notNil);
};

export const measureCanvasNode = (
  element: Element,
  cellId: string,
  zIndex?: number,
): CanvasNode => {
  const { x, y, width, height } = element.getBoundingClientRect();
  return {
    id: cellId,
    position: { x, y },
    measured: { width, height },
    zIndex: zIndex ?? 1,
    precomputed: true,
  };
};
